// src/cockpit4/components/BlogPublishPanel.tsx
// ============================================
// v6.25 官博发布面板：
//   写 Markdown/HTML → 实时预览 → 发布到官网 www.xshetech.com/blog/
//   已发布文章支持「编辑」读回 → 右侧「直接发布到新闻资讯」按钮覆盖更新
//   后端 /blog-api/publish 生成文章页 + 更新 blog.html 列表 + sitemap
//   后端 /blog-api/read?slug= 读回 Markdown 源供编辑
// ============================================

import { useState, useEffect, useCallback } from "react";
import { Send, ExternalLink, RefreshCw, FileText, Eye, PenLine, Globe } from "lucide-react";

/** 简单 Markdown → HTML（标题/列表/引用/代码块/加粗/斜体/行内代码）。输入文本统一转义防 XSS。 */
function inlineFormat(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>");
}

export function markdownToHtml(md: string): string {
  if (!md) return "";
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  let inCode = false;
  const codeBuf: string[] = [];
  for (const line of lines) {
    if (line.trim().startsWith("```")) {
      if (inCode) {
        html += `<pre><code>${esc(codeBuf.join("\n"))}</code></pre>`;
        codeBuf.length = 0;
        inCode = false;
      } else {
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h${h[1].length}>${inlineFormat(esc(h[2]))}</h${h[1].length}>`;
      continue;
    }
    const li = line.match(/^\s*[-*]\s+(.*)/);
    if (li) {
      if (!inList) { html += "<ul>"; inList = true; }
      html += `<li>${inlineFormat(esc(li[1]))}</li>`;
      continue;
    }
    if (inList) { html += "</ul>"; inList = false; }
    if (line.trim().startsWith(">")) {
      html += `<blockquote style="border-left:3px solid #8b5cf6;padding-left:12px;color:#9aa7b5;margin:8px 0;">${inlineFormat(esc(line.trim().slice(1).trim()))}</blockquote>`;
      continue;
    }
    if (!line.trim()) continue;
    html += `<p>${inlineFormat(esc(line.trim()))}</p>`;
  }
  if (inList) html += "</ul>";
  if (inCode) html += `<pre><code>${esc(codeBuf.join("\n"))}</code></pre>`;
  return html;
}

interface PublishedPost {
  slug: string;
  title: string;
  url: string;
}

export default function BlogPublishPanel() {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("GEO+DSO");
  const [slug, setSlug] = useState("");
  const [contentMd, setContentMd] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [posts, setPosts] = useState<PublishedPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch("/blog-api/list");
      const data = await resp.json();
      if (data?.success) setPosts(data.posts || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const autoSlug = () => {
    const ascii = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    return ascii || `post-${Date.now()}`;
  };

  const publish = async () => {
    if (!title.trim() || !contentMd.trim()) {
      setResult({ ok: false, msg: "标题与正文不能为空" });
      return;
    }
    setPublishing(true);
    setResult(null);
    try {
      const contentHtml = markdownToHtml(contentMd);
      const resp = await fetch("/blog-api/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          slug: slug.trim() || autoSlug(),
          category: category.trim(),
          contentHtml,
          contentMd: contentMd,
        }),
      });
      const data = await resp.json();
      if (data?.success) {
        setResult({ ok: true, msg: (editing ? "已更新：" : "发布成功：") + data.url });
        setTitle("");
        setContentMd("");
        setSlug("");
        setCategory("GEO+DSO");
        setEditing(null);
        loadPosts();
      } else {
        setResult({ ok: false, msg: data?.error || "发布失败" });
      }
    } catch (e) {
      setResult({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setPublishing(false);
    }
  };

  const editPost = async (slugValue: string) => {
    setResult(null);
    try {
      const resp = await fetch("/blog-api/read?slug=" + encodeURIComponent(slugValue));
      const data = await resp.json();
      if (data?.success && data?.post) {
        const p = data.post;
        setTitle(p.title || "");
        setCategory(p.category || "未分类");
        setSlug(p.slug || slugValue);
        setContentMd(p.contentMd || "");
        setEditing(p.slug || slugValue);
      } else {
        setResult({ ok: false, msg: data?.error || "读取失败" });
      }
    } catch (e) {
      setResult({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    }
  };

  const previewHtml = markdownToHtml(contentMd);

  return (
    <div className="space-y-3">
      {/* 顶部说明 */}
      <div className="flex items-center gap-2 text-[10px] font-mono text-gray-500">
        <Globe size={11} className="text-cyan-400" />
        <span>官博发布：内容将生成静态文章页，同步到官网 <span className="text-cyan-300">www.xshetech.com/blog</span>，并自动更新列表与 sitemap</span>
      </div>

      {/* 编辑中提示 */}
      {editing && (
        <div className="flex items-center gap-2 rounded-md border border-cyan-500/30 bg-cyan-500/[0.06] px-3 py-2 text-[11px] text-cyan-200">
          <PenLine size={11} />
          <span>正在编辑：<b className="text-white">{title}</b>（slug: {editing}）— 改完点右侧「直接发布到新闻资讯」即可覆盖更新</span>
          <button
            onClick={() => { setEditing(null); setTitle(""); setContentMd(""); setSlug(""); setCategory("GEO+DSO"); }}
            className="ml-auto text-[10px] text-gray-400 hover:text-white underline"
          >
            退出编辑
          </button>
        </div>
      )}

      {/* 表单卡片 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-4 space-y-3">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-7">
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">文章标题</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例如：企业AI系统部署到底要踩多少坑"
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white placeholder-gray-600 outline-none focus:border-cyan-500/40"
            />
          </div>
          <div className="col-span-5">
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">分类</label>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="GEO+DSO / 酒店数字化 / 企业AI"
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white placeholder-gray-600 outline-none focus:border-cyan-500/40"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
            URL 标识（slug，留空自动生成；编辑时已锁定为原 slug）
          </label>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="小写字母/数字/连字符，如：ai-system-deploy-pitfalls"
            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white placeholder-gray-600 outline-none focus:border-cyan-500/40 font-mono"
          />
        </div>

        <div>
          <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
            正文（支持 Markdown：# 标题 / - 列表 / **加粗** / &gt; 引用 / ``` 代码块）
          </label>
          <textarea
            value={contentMd}
            onChange={(e) => setContentMd(e.target.value)}
            rows={10}
            placeholder={"# 开头写个标题\n\n正文段落…\n\n- 要点1\n- 要点2\n\n> 金句引用"}
            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white placeholder-gray-600 outline-none focus:border-cyan-500/40 resize-y font-mono leading-relaxed"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={publish}
            disabled={publishing}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-mono uppercase tracking-wider bg-gradient-to-r from-purple-500 to-cyan-500 text-white disabled:opacity-40 transition-all hover:brightness-110"
          >
            <Send size={12} />
            {publishing ? "发布中…" : (editing ? "更新发布" : "发布到官博")}
          </button>
          <button
            onClick={() => setSlug("")}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.04] border border-white/[0.06]"
          >
            <RefreshCw size={11} />
            重置 slug
          </button>
          {result && (
            <span className={`text-[11px] ${result.ok ? "text-emerald-400" : "text-red-400"}`}>
              {result.msg}
            </span>
          )}
        </div>
      </div>

      {/* 预览卡片 + 右侧直接发布 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Eye size={12} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">实时预览</span>
          <button
            onClick={publish}
            disabled={publishing}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-mono bg-gradient-to-r from-purple-500 to-cyan-500 text-white disabled:opacity-40 hover:brightness-110"
          >
            <Send size={11} />
            {publishing ? "发布中…" : "直接发布到新闻资讯"}
          </button>
        </div>
        <div
          className="min-h-[120px] rounded-md bg-white/[0.02] border border-white/[0.04] p-4 text-[13px] text-gray-200 leading-relaxed overflow-auto"
          style={{ maxHeight: 280 }}
          dangerouslySetInnerHTML={{ __html: previewHtml || "<p style='color:#5b6772;'>预览区…</p>" }}
        />
      </div>

      {/* 已发布列表 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={12} className="text-purple-400" />
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            已发布文章（{posts.length}）— 点「编辑」读回正文
          </span>
        </div>
        {loading ? (
          <div className="text-[12px] text-gray-500 py-4 text-center">加载中…</div>
        ) : posts.length === 0 ? (
          <div className="text-[12px] text-gray-500 py-4 text-center">暂无已发布文章</div>
        ) : (
          <div className="space-y-2">
            {posts.map((post) => (
              <div key={post.slug} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.05]">
                <PenLine size={11} className="text-purple-400 flex-shrink-0" />
                <span className="flex-1 text-[12px] text-gray-200 truncate">{post.title}</span>
                <button
                  onClick={() => editPost(post.slug)}
                  className="flex items-center gap-1 text-[10px] text-amber-300 hover:underline flex-shrink-0 border border-amber-400/30 rounded px-1.5 py-0.5"
                >
                  编辑
                </button>
                <a
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[10px] text-cyan-300 hover:underline flex-shrink-0"
                >
                  打开 <ExternalLink size={9} />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
